import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import {
  currentPinningSessionAtom,
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pinningEnabledArtworksAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  type DesignLabPlacementKey,
  useCreatePinningSession
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinsCtaClicked,
  trackDesignLabPinningModeEntered
} from "@design-lab/shared/lib/analytics";
import { usePinBasedRedrawEnabled } from "@design-lab/shared/lib/config";
import { useToast } from "@design-lab/shared/ui";
import { StarsIcon } from "@design-lab/shared/ui/icons";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useWatch } from "react-hook-form";
import type { ArtworkForm } from "@design-lab/shared/types";
import type { models } from "jiffy-api";
import { CompletedSessionIndicator } from "./CompletedSessionIndicator";

type RequestDesignChangesProps = {
  placementSide?: "front" | "back";
  currentValue?: models.cart.madeAdd.EnhancementTier | undefined;
  onChange: (value: models.cart.madeAdd.EnhancementTier) => void;
};

export function RequestDesignChanges({
  placementSide = "front",
  currentValue,
  onChange
}: RequestDesignChangesProps) {
  const { form } = useArtworkForm();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const { mutate: createPinningSession, isPending: isCreatingPinningSession } =
    useCreatePinningSession();
  const { error: showErrorToast } = useToast();
  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();
  const pinningEnabledArtworks = useAtomValue(pinningEnabledArtworksAtom);

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  const selectedFrontPlacement = artworkForm?.selectedFrontPlacement as
    | DesignLabPlacementKey
    | undefined;
  let placementKey: DesignLabPlacementKey = "front";
  if (placementSide === "back") {
    placementKey = "back";
  } else if (selectedFrontPlacement === "frontLeftChest") {
    placementKey = "frontLeftChest";
  }

  const artworkIdForm = (
    artworkForm?.[placementKey]?.artworkId ? String(artworkForm[placementKey]?.artworkId) : ""
  ) as string;

  // Check if pinning is enabled for this artwork (based on ownership)
  // If artworkId is not in the map, assume enabled (backwards compatibility)
  const isPinningEnabled = artworkIdForm ? (pinningEnabledArtworks[artworkIdForm] ?? true) : true;

  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions
  });
  const hasActiveSession = pinBasedRedrawEnabled && sessionForPlacement?.state === "active";
  const hasCompletedSession = pinBasedRedrawEnabled && sessionForPlacement?.state === "completed";

  const handleRequestDesignerEdit = useCallback(() => {
    if (isCreatingPinningSession) return;
    const artworkId = artworkIdForm;
    if (!artworkId) return;
    if (!designProjectPlacementId) {
      showErrorToast({
        title: "Unable to start design instructions",
        description: "Please wait a moment and try again."
      });
      return;
    }

    // First, switch to "Print As Is" if not already selected
    if (currentValue !== "print_original") {
      onChange("print_original");
    }

    trackDesignLabPinsCtaClicked(artworkId);

    // Reuse existing active session if one exists for this artwork
    const existingSession = sessionForPlacement;
    if (existingSession?.state === "active") {
      trackDesignLabPinningModeEntered({
        artworkId,
        sessionId: existingSession.sessionId,
        hasExistingPins: true
      });
      setCurrentPinningSession({
        designProjectPlacementId,
        artworkId,
        sessionId: existingSession.sessionId
      });
      setPinningMode(true);
      return;
    }

    createPinningSession(
      { designProjectPlacementId, artworkId },
      {
        onSuccess: (data: { sessionId: string }) => {
          trackDesignLabPinningModeEntered({
            artworkId,
            sessionId: data.sessionId,
            hasExistingPins: false
          });
          setPinningSessions((prev: typeof pinningSessions) => ({
            ...prev,
            [designProjectPlacementId]: {
              sessionId: data.sessionId,
              state: "active",
              summary: null
            }
          }));
          setCurrentPinningSession({
            designProjectPlacementId,
            artworkId,
            sessionId: data.sessionId
          });
          setPinningMode(true);
        },
        onError: () => {
          showErrorToast({
            title: "Unable to start design instructions",
            description: "Something went wrong. Please try again."
          });
        }
      }
    );
  }, [
    isCreatingPinningSession,
    artworkIdForm,
    designProjectPlacementId,
    currentValue,
    onChange,
    sessionForPlacement,
    createPinningSession,
    setPinningSessions,
    setCurrentPinningSession,
    setPinningMode,
    showErrorToast
  ]);

  // Completed pinning session indicator
  if (hasCompletedSession) {
    return <CompletedSessionIndicator placementKey={placementKey} placementSide={placementSide} />;
  }

  if (!pinBasedRedrawEnabled || hasActiveSession || !isPinningEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 pt-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-neutral-950">
          How can we make your image better?
        </span>
      </div>
      <button
        type="button"
        data-testid="request-designer-edit-button-print-as-is"
        className="border-cta-gradient flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg !border-[1px] p-3 text-sm font-medium text-neutral-950 hover:![background-image:linear-gradient(#f3f4f6,#f3f4f6),linear-gradient(103.21deg,#a855f7_6.2%,#2563eb_108.53%)]"
        onClick={handleRequestDesignerEdit}
        disabled={isCreatingPinningSession}
      >
        <StarsIcon className="h-5 w-5" />
        <span>Request Design Changes</span>
      </button>
    </div>
  );
}
